/**
 * Semantic Analyzer Utilities
 *
 * Detects component roles and suggests HTML tags.
 */

import type { ComponentRole, HtmlTag } from '../types/component-tree.js'
import type { SceneNode } from '../types/scene.js'
import type { TextProps } from '../types/properties/text.js'

/**
 * Detect if a frame is organizational/structural rather than visual.
 *
 * Uses conservative heuristics to identify frames that are for Figma organization
 * rather than visual implementation.
 *
 * Detection patterns:
 * - Generic auto-generated names: "Frame 12345", "Group", "Container"
 * - Single-child passthrough wrappers with no meaningful layout
 *
 * @param node - The Figma node to check
 * @returns true if the frame is structural/organizational
 */
export function isStructuralFrame(node: SceneNode): boolean {
  if (node.type !== 'FRAME') return false

  const name = node.name.trim()

  // Pattern 1: Generic auto-generated names
  // Matches: "Frame", "Frame 123", "Group", "Group 456", "Container", "Container 789"
  if (/^(Frame|Group|Container)\s*\d*$/i.test(name)) {
    return true
  }

  // Pattern 2: Single-child passthrough with no meaningful layout
  if (node.children?.length === 1) {
    const props = node.properties as any

    // Check if frame has no meaningful layout properties
    const hasLayout =
      props.stackMode ||
      (props.stackSpacing && props.stackSpacing > 0) ||
      (props.stackHorizontalPadding && props.stackHorizontalPadding > 0) ||
      (props.stackVerticalPadding && props.stackVerticalPadding > 0)

    if (!hasLayout) {
      return true
    }
  }

  return false
}

/**
 * Detect if a node is an icon component and extract its type.
 *
 * Uses conservative name-based detection to identify icon components.
 *
 * Detection patterns:
 * - "Icon / Pin", "Icon / ArrowDown" (slash-separated)
 * - "icon-menu", "icon-close" (dash-separated)
 * - "icon_search", "icon_settings" (underscore-separated)
 * - Component/instance nodes with "icon" in name
 *
 * @param node - The Figma node to check
 * @returns The exact icon name if detected, undefined otherwise
 */
export function detectIconType(node: SceneNode): string | undefined {
  const name = node.name.trim()

  // Pattern 1: Explicit icon prefix with separator
  // Matches: "Icon / Pin", "icon-arrow", "icon_menu"
  if (/^icon\s*[\/\-_]/i.test(name)) {
    return name
  }

  // Pattern 2: Component/instance with "icon" in name
  if ((node.type === 'INSTANCE' || node.type === 'SYMBOL') && /icon/i.test(name)) {
    return name
  }

  return undefined
}

/**
 * Detect component role from node name
 */
export function detectComponentRole(node: SceneNode): ComponentRole | undefined {
  const name = node.name.toLowerCase()

  // Icon detection (use consistent detection logic)
  const iconType = detectIconType(node)
  if (iconType) {
    return 'icon'
  }

  // Button detection
  if (name.includes('button') || name.includes('btn')) {
    return 'button'
  }

  // Input detection
  if (name.includes('input') || name.includes('field') || name.includes('textfield')) {
    return 'input'
  }

  // Card detection
  if (name.includes('card')) {
    return 'card'
  }

  // Navigation detection
  if (name.includes('nav') || name.includes('menu') || name.includes('sidebar')) {
    return 'navigation'
  }

  // Header detection
  if (name.includes('header') || name.includes('title')) {
    return 'header'
  }

  // Badge detection
  if (name.includes('badge') || name.includes('tag') || name.includes('label')) {
    return 'badge'
  }

  // Avatar detection
  if (name.includes('avatar') || name.includes('profile')) {
    return 'avatar'
  }

  return undefined
}

/**
 * Suggest HTML tag based on node type and role
 */
export function suggestHtmlTag(node: SceneNode, role?: ComponentRole): HtmlTag {
  // Role-based suggestions
  if (role === 'button') return 'button'
  if (role === 'input') return 'input'
  if (role === 'navigation') return 'nav'
  if (role === 'header') return 'header'

  // Type-based suggestions
  if (node.type === 'TEXT') {
    const props = node.properties as TextProps
    const fontSize = props.fontSize

    // Heading sizes (approximate)
    if (fontSize && fontSize >= 32) return 'h1'
    if (fontSize && fontSize >= 24) return 'h2'
    if (fontSize && fontSize >= 20) return 'h3'
    if (fontSize && fontSize >= 18) return 'h4'

    // Check if it's a heading by name
    const name = node.name.toLowerCase()
    if (name.includes('heading') || name.includes('title')) {
      return 'h2'
    }

    // Default text
    return 'p'
  }

  // Default to div for containers
  return 'div'
}
